/**
 * Standard License Plates Section
 * 
 * Strict 3-Step Flow:
 * 1. Vehicle Type (Select)
 * 2. Plate Size (Select - Disabled until vehicle selected)
 * 3. Plate Type (Select)
 */

import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useCartStore } from '@/store/cartStore'
import { type VehicleCategory, type PlateType, type PlateSize, findRule } from '@/services/rules/plateRulesEngine'
import { searchPlates, validateSearchCriteria, type SearchCriteria, type SearchResult } from '@/services/search/plateSearchService'
import { getPlateSizeLabel, getValidSizesForVehicle } from '@/services/rules/vehicleSizeMapping'
import InteractivePlatePreview from './InteractivePlatePreview'
import registrationOfficesData from '@/data/registrationOffices.json'

interface VehicleType {
  id: VehicleCategory
  label: string
  icon: string
  description: string
}

const VEHICLE_TYPES: VehicleType[] = [
  {
    id: 'PKW',
    label: 'PKW (Personenkraftwagen)',
    icon: '🚗',
    description: 'Kennzeichen für Personenkraftwagen - alle Standardgrößen verfügbar',
  },
  {
    id: 'TRUCK',
    label: 'LKW (Lastkraftwagen)',
    icon: '🚛',
    description: 'Kennzeichen für Lastkraftwagen - alle Standardgrößen verfügbar',
  },
  {
    id: 'MOTORCYCLE',
    label: 'Motorrad',
    icon: '🏍️',
    description: 'Kennzeichen für Motorräder - Zweizeilig und Einzeilig verfügbar',
  },
  {
    id: 'LEICHTKRAFT',
    label: 'Leichtkraftrad 125cc',
    icon: '🛵',
    description: 'Kennzeichen für Leichtkrafträder (125cc) - Spezielle Größen',
  },
  {
    id: 'TRAILER',
    label: 'Anhänger',
    icon: '🚚',
    description: 'Kennzeichen für Anhänger - Standardgrößen verfügbar',
  },
  {
    id: 'TRAKTOR',
    label: 'Landwirtschaft / Traktor',
    icon: '🚜',
    description: 'Kennzeichen für Traktoren und landwirtschaftliche Fahrzeuge',
  },
]

const PLATE_TYPES: { value: PlateType; label: string }[] = [
  { value: 'REGULAR', label: 'Standard' },
  { value: 'REGULAR_SEASON', label: 'Saison' },
  { value: 'E', label: 'E-Kennzeichen' },
  { value: 'E_SEASON', label: 'E-Saison' },
  { value: 'H', label: 'H-Kennzeichen' },
  { value: 'H_SEASON', label: 'H-Saison' },
]

interface StandardLicensePlatesSectionProps {
  mode?: 'standard' | 'individual'
}

const StandardLicensePlatesSection = ({ mode = 'standard' }: StandardLicensePlatesSectionProps) => {
  // Step 1: Vehicle
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleCategory | null>(null)

  // Step 2: Size
  const [selectedPlateSize, setSelectedPlateSize] = useState<PlateSize | null>(null)

  // Step 3: Type
  const [selectedPlateType, setSelectedPlateType] = useState<PlateType | null>(null)

  // Individual Mode State
  const [isReserved, setIsReserved] = useState(false)

  // Search state
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>({
    cityCode: '',
    letters: '',
    numbers: '',
  })
  const [registrationOfficeServiceId, setRegistrationOfficeServiceId] = useState<number | string>('')
  const [selectedOfficeName, setSelectedOfficeName] = useState<string>('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [searchProgress, setSearchProgress] = useState(0)
  const [searchError, setSearchError] = useState<string | null>(null)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const [matchingOffices, setMatchingOffices] = useState<any[]>([])

  const navigate = useNavigate()
  const addItem = useCartStore((state) => state.addItem)

  const handleAddToCart = (result: SearchResult) => {
    const plateText = `${result.cityCode}-${result.letters} ${result.numbers}`

    // Create Cart Item
    const cartItem = {
      productId: `PLATE-${result.cityCode}-${result.letters}-${result.numbers}-${Date.now()}`,
      productName: `Kennzeichen ${plateText}`,
      price: 14.95, // Standard price
      quantity: 1,
      customization: {
        plateType: result.plateType,
        vehicleCategory: result.vehicleCategory,
        size: result.plateSize,
        registrationOfficeServiceId,
        cityCode: result.cityCode,
        letters: result.letters,
        numbers: result.numbers,
        fullText: plateText
      }
    }

    addItem(cartItem)
    toast.success(`${plateText} zum Warenkorb hinzugefügt!`)

    // Navigate to checkout as requested
    setTimeout(() => {
      navigate('/checkout')
    }, 500)
  }

  // Auto-set Registration Office ID based on City Code
  useEffect(() => {
    const city = (searchCriteria.cityCode || '').toUpperCase()
    if (!city) {
      setSelectedOfficeName('')
      setMatchingOffices([])
      return
    }

    // Find office by region code
    // Iterate to find all matches (City vs Landkreis)
    const offices = registrationOfficesData.filter((o: any) => o.regionCode === city)
    setMatchingOffices(offices)

    let office = offices[0]

    // CRITICAL FIX: For Göttingen (GÖ), the 'Stadt' office (ID 313) returns 503.
    // We must prioritize the 'Leine (Landkreis)' or 'Landkreis' office (ID 779) which is likely the active service.
    if (city === 'GÖ') {
      const landkreisOffice = offices.find((o: any) => o.name.includes('Landkreis') || o.name.includes('Göttingen'))
      // ID 779 is known to be the Landkreis entry in our data
      const preferred = offices.find((o: any) => o.id === 779) || landkreisOffice
      if (preferred) office = preferred
    }

    if (office) {
      setRegistrationOfficeServiceId(office.id)
      setSelectedOfficeName(office.name)
    } else {
      // If no exact match, maybe clear name but keep ID? 
      // Or clear both? Clearing ID prevents false positives.
      setRegistrationOfficeServiceId('')
      setSelectedOfficeName('')
    }
  }, [searchCriteria.cityCode])

  // Handle Vehicle Change
  const handleVehicleChange = (vehicleId: VehicleCategory) => {
    setSelectedVehicle(vehicleId)
    setSelectedPlateSize(null) // Reset size as it depends on vehicle
    // We keep Plate Type as it might still be valid, or user might want to keep it.
    // However, if we want strict flow, we could reset it too. But usually type (Standard/Season) is independent of vehicle.
    // The sizes definitely depend on vehicle.
  }

  // Handle Size Change
  const handleSizeChange = (sizeStr: string) => {
    if (!selectedVehicle) return
    const validSizes = getValidSizesForVehicle(selectedVehicle)
    const size = validSizes.find(s => `${s.width}x${s.height}` === sizeStr)
    setSelectedPlateSize(size || null)
  }

  // Handle Reserve (Individual Mode)
  const handleReserve = (plateInput: any) => {
    const rule = findRule(selectedVehicle!, selectedPlateSize!, selectedPlateType!)
    if (!rule) {
      toast.error("Fehler: Ungültige Konfiguration")
      return
    }

    // Construct search result-like object
    const result: SearchResult = {
      cityCode: plateInput.cityCode,
      letters: plateInput.letters,
      numbers: plateInput.numbers,
      plateType: selectedPlateType!,
      vehicleCategory: selectedVehicle!,
      plateSize: selectedPlateSize!,
      rule
    }

    setIsReserved(true)
    handleAddToCart(result)
  }

  // Auto-search with debounce
  useEffect(() => {
    // SKIP SEARCH IF INDIVIDUAL MODE
    if (mode === 'individual') return

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    const hasInput = searchCriteria.cityCode || searchCriteria.letters || searchCriteria.numbers
    if (!hasInput) {
      setSearchResults([])
      setIsSearching(false)
      setSearchError(null)
      return
    }

    // Prevent search if ID doesn't match the city code (prevents transient 400 errors)
    // The auto-lookup effect will update the ID momentarily.
    // Allow ANY office that matches the region code (e.g. Göttingen has IDs 313 and 779 for "GÖ")
    const matchingOffices = registrationOfficesData.filter((o: any) => o.regionCode === searchCriteria.cityCode?.toUpperCase())
    const isIdValid = matchingOffices.some((o: any) => o.id === parseInt(String(registrationOfficeServiceId)))

    if (matchingOffices.length > 0 && !isIdValid) {
      return
    }

    if (!selectedVehicle || !selectedPlateSize || !selectedPlateType) {
      setSearchError('Bitte schließen Sie die Konfiguration ab (Schritt 1-3)')
      setSearchResults([])
      setIsSearching(false)
      return
    }

    const validation = validateSearchCriteria(
      { ...searchCriteria, registrationOfficeServiceId },
      selectedVehicle,
      selectedPlateSize,
      selectedPlateType
    )

    if (!validation.valid) {
      setSearchError(validation.error || 'Ungültige Suchkriterien')
      setSearchResults([])
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    setSearchProgress(0)
    setSearchError(null)

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await searchPlates(
          {
            ...searchCriteria,
            vehicleCategory: selectedVehicle,
            plateSize: selectedPlateSize,
            plateType: selectedPlateType,
            registrationOfficeServiceId,
          },
          (progress) => setSearchProgress(progress)
        )
        setSearchResults(results)
        setSearchError(null)
      } catch (error: any) {
        console.error('Search error:', error)
        let errorMessage = 'Fehler bei der Suche. Bitte versuchen Sie es erneut.'
        if (error.message) errorMessage = error.message
        setSearchError(errorMessage)
        setSearchResults([])
      } finally {
        setIsSearching(false)
        setSearchProgress(0)
      }
    }, 500)

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    }
  }, [searchCriteria, selectedVehicle, selectedPlateSize, selectedPlateType, registrationOfficeServiceId, mode])

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-white mb-2">
          {mode === 'individual' ? 'Individuelle Kennzeichen' : 'Standard-Kennzeichen'}
        </h2>
        <p className="text-gray-400">
          {mode === 'individual'
            ? 'Gestalten Sie Ihr Wunschkennzeichen (ohne Reservierung)'
            : 'Konfigurieren Sie Ihr Kennzeichen in 3 Schritten'}
        </p>
      </div>

      <div className="bg-dark-900 rounded-2xl p-6 sm:p-8 border border-gray-800 space-y-8 shadow-xl">
        {/* The 3-Step Flow */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* Step 1: Vehicle Type */}
          <div className="space-y-3">
            <label className="text-sm font-semibold text-gray-300 flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-500/20 text-primary-400 text-xs text-center border border-primary-500/30">1</span>
              Fahrzeugtyp
            </label>
            <select
              value={selectedVehicle || ''}
              onChange={(e) => handleVehicleChange(e.target.value as VehicleCategory)}
              disabled={isReserved}
              className={`w-full px-4 py-3 bg-dark-950 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all ${isReserved ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-600'}`}
            >
              <option value="">Bitte wählen...</option>
              {VEHICLE_TYPES.map((vehicle) => (
                <option key={vehicle.id} value={vehicle.id}>
                  {vehicle.label}
                </option>
              ))}
            </select>
          </div>

          {/* Step 2: Plate Size */}
          <div className="space-y-3">
            <label className={`text-sm font-semibold flex items-center gap-2 ${!selectedVehicle ? 'text-gray-600' : 'text-gray-300'}`}>
              <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs text-center border ${!selectedVehicle ? 'bg-gray-800 text-gray-600 border-gray-700' : 'bg-primary-500/20 text-primary-400 border-primary-500/30'}`}>2</span>
              Größe
            </label>
            <select
              value={selectedPlateSize ? `${selectedPlateSize.width}x${selectedPlateSize.height}` : ''}
              onChange={(e) => handleSizeChange(e.target.value)}
              disabled={!selectedVehicle || isReserved}
              className={`w-full px-4 py-3 bg-dark-950 border rounded-xl text-white focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all ${!selectedVehicle || isReserved
                ? 'border-gray-800 opacity-50 cursor-not-allowed text-gray-500'
                : 'border-gray-700 cursor-pointer hover:border-gray-600'
                }`}
            >
              <option value="">{!selectedVehicle ? 'Zuerst Fahrzeug wählen' : 'Bitte wählen...'}</option>
              {selectedVehicle && getValidSizesForVehicle(selectedVehicle).map((size) => (
                <option key={`${size.width}x${size.height}`} value={`${size.width}x${size.height}`}>
                  {getPlateSizeLabel(size)}
                </option>
              ))}
            </select>
          </div>

          {/* Step 3: Plate Type */}
          <div className="space-y-3">
            <label className={`text-sm font-semibold flex items-center gap-2 ${!selectedPlateSize ? 'text-gray-500' : 'text-gray-300'}`}>
              <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs text-center border ${!selectedPlateSize ? 'bg-gray-800 text-gray-600 border-gray-700' : 'bg-primary-500/20 text-primary-400 border-primary-500/30'}`}>3</span>
              Kennzeichentyp
            </label>
            <select
              value={selectedPlateType || ''}
              onChange={(e) => setSelectedPlateType(e.target.value as PlateType)}
              disabled={isReserved}
              // Optional: Disable if previous steps not done, though user requirement didn't strictly say step 3 is disabled.
              // Logicwise it's seamless if enabled, but visually better if guided.
              // I'll leave it enabled but styled slightly different if previous not ready? 
              // Actually for "Strict Flow", disabling until Size is picked is very common.
              // But user only explicitly said "Step 2... disabled until vehicle".
              // I'll keep it enabled to not over-restrict unless necessary, providing clear labels.
              className={`w-full px-4 py-3 bg-dark-950 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all ${isReserved ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-600'}`}
            >
              <option value="">Bitte wählen...</option>
              {PLATE_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Configuration Summary & Additional Inputs */}
        <AnimatePresence>
          {selectedVehicle && selectedPlateSize && selectedPlateType && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="border-t border-gray-800 pt-8"
            >
              <div className="space-y-12">

                {/* 1. Registration Office Input (Centered) */}
                <div className="max-w-md mx-auto w-full space-y-4">

                  {/* Office ID / Selection */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2 text-center">
                      Zulassungsstelle
                    </label>

                    {matchingOffices.length > 1 ? (
                      <div className="space-y-2">
                        <select
                          value={registrationOfficeServiceId}
                          onChange={(e) => {
                            const newId = e.target.value
                            setRegistrationOfficeServiceId(newId)
                            const office = matchingOffices.find((o: any) => String(o.id) === String(newId))
                            if (office) setSelectedOfficeName(office.name)
                          }}
                          disabled={isReserved}
                          className={`w-full px-4 py-3 bg-dark-950 border border-primary-500/50 rounded-xl text-white focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 cursor-pointer shadow-[0_0_15px_rgba(59,130,246,0.15)] ${isReserved ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <option value="">Bitte wählen...</option>
                          {matchingOffices.map((office: any) => (
                            <option key={office.id} value={office.id}>
                              {office.name} ({office.id})
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-yellow-400/80 text-center">
                          ℹ️ Bitte wählen Sie die korrekte Zulassungsstelle (Stadt oder Landkreis)
                        </p>
                      </div>
                    ) : (
                      <div className="relative">
                        <input
                          type="text"
                          value={registrationOfficeServiceId}
                          onChange={(e) => setRegistrationOfficeServiceId(e.target.value)}
                          placeholder="ID"
                          readOnly={!!selectedOfficeName || isReserved} // Read-only if auto-detected
                          className={`w-full px-4 py-3 bg-dark-950 border border-gray-700 rounded-xl text-white text-center focus:outline-none focus:border-primary-500 ${selectedOfficeName || isReserved ? 'opacity-80 cursor-default' : ''}`}
                        />
                        {selectedOfficeName && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500">
                            ✓
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {selectedOfficeName && matchingOffices.length <= 1 && (
                    <p className="text-sm text-green-400 flex items-center justify-center gap-2">
                      <span>✓</span> {selectedOfficeName}
                    </p>
                  )}
                </div>

                {/* 2. Interactive Plate Preview (Full Width/Centered) */}
                <div className="w-full">
                  <label className="block text-sm font-semibold text-gray-300 mb-4 text-center">
                    Kennzeichen Eingabe
                  </label>
                  <div className="flex justify-center bg-dark-950/50 p-6 md:p-10 rounded-2xl border border-gray-800/50 max-w-4xl mx-auto shadow-inner">
                    {(() => {
                      const rule = findRule(selectedVehicle, selectedPlateSize, selectedPlateType)
                      if (!rule) return <div className="text-red-400">Keine gültige Regel gefunden</div>

                      return (
                        <InteractivePlatePreview
                          plateSize={selectedPlateSize}
                          plateInput={{
                            cityCode: searchCriteria.cityCode || '',
                            letters: searchCriteria.letters || '',
                            numbers: searchCriteria.numbers || '',
                          }}
                          plateType={selectedPlateType}
                          vehicleCategory={selectedVehicle}
                          rule={rule}
                          onInputChange={(input) => {
                            if (isReserved) return // Lock input
                            setSearchCriteria({
                              cityCode: input.cityCode,
                              letters: input.letters,
                              numbers: input.numbers,
                            })
                          }}
                          onReserve={mode === 'individual' ? handleReserve : undefined}
                        />
                      )
                    })()}
                  </div>
                </div>

                {/* 3. Search Results & Status (Centered) */}
                {mode === 'standard' && (
                  <div className="max-w-2xl mx-auto space-y-6">

                    {/* Status/Error */}
                    <div className="min-h-[60px]">
                      {searchError ? (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="p-4 bg-red-900/20 border border-red-500/30 rounded-xl text-red-200 text-sm text-center"
                        >
                          ⚠️ {searchError}
                        </motion.div>
                      ) : isSearching ? (
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm text-gray-400">
                            <span>Prüfe Verfügbarkeit...</span>
                            <span>{Math.round(searchProgress)}%</span>
                          </div>
                          <div className="w-full bg-dark-800 rounded-full h-2 overflow-hidden">
                            <motion.div
                              className="h-full bg-gradient-to-r from-primary-500 to-primary-600"
                              initial={{ width: 0 }}
                              animate={{ width: `${searchProgress}%` }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500 italic text-center">
                          Automatischer Verfügbarkeitcheck aktiv
                        </div>
                      )}
                    </div>

                    {/* Results List */}
                    {searchResults.length > 0 && (
                      <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-gray-300 text-center">
                          Gefundene Varianten ({searchResults.length})
                        </h3>
                        <div className="grid grid-cols-1 gap-3">
                          {searchResults.map((result, index) => (
                            <motion.div
                              key={index}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="bg-dark-950/80 border border-gray-700 rounded-xl p-4 flex items-center justify-between group hover:border-primary-500/50 transition-all cursor-pointer hover:bg-dark-900"
                              onClick={() => {
                                setSearchCriteria({
                                  ...searchCriteria,
                                  cityCode: result.cityCode,
                                  letters: result.letters,
                                  numbers: result.numbers
                                })
                              }}
                            >
                              <div className="flex items-center gap-4">
                                <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]"></div>
                                <span className="font-mono text-xl text-white font-bold tracking-wider">
                                  {result.cityCode}-{result.letters} {result.numbers}
                                </span>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="text-xs text-gray-500 bg-dark-900 px-2 py-1 rounded border border-gray-800 hidden sm:block">
                                  {getPlateSizeLabel(result.plateSize)}
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation() // Prevent row click
                                    handleAddToCart(result)
                                  }}
                                  className="px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white text-sm font-bold rounded-lg shadow-lg shadow-primary-500/20 transition-all transform hover:scale-105"
                                >
                                  Reservieren
                                </button>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}

                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default StandardLicensePlatesSection
